import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WhatsappLogo, Phone, EnvelopeSimple, MapPin, PaperPlaneTilt, CheckCircle, Storefront, Clock, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BRAND, waLink, isOpenNow } from "@/lib/brand";

export default function PublicContact() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const open = isOpenNow();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) { toast.error("Name and mobile required"); return; }
    setBusy(true);
    try {
      await api.post("/leads/public", {
        name: form.name, mobile: form.mobile, email: form.email,
        message: form.message, service_hint: "contact-form", source: "contact",
        landing_page: "/contact",
      });
      setDone(true);
      toast.success("Message received — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-14" data-testid="public-contact">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Get in Touch</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>We'd love to hear from you.</h1>
        <p className="text-slate-600 mt-2">Visit us at our Thane branch, WhatsApp us, or drop a message — whichever works best for you.</p>
      </div>

      {/* ================= Contact Card ================= */}
      <Card className="mb-10 overflow-hidden bg-white border-slate-200 shadow-lg" data-testid="contact-card">
        <div className="grid md:grid-cols-5">
          {/* Left — brand + address */}
          <div className="md:col-span-3 p-8 md:p-10 bg-gradient-to-br from-[#1e2f6b] via-[#1e3a8a] to-[#3b82f6] text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-orange-500/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-semibold text-orange-300">
                  <Storefront size={12} weight="fill" /> {BRAND.authorization}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[9px] uppercase tracking-widest font-bold" data-testid="badge-head-office">
                  🏢 Head Office
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                {BRAND.legalName}
              </h2>

              {/* Address */}
              <div className="mb-6 flex items-start gap-3">
                <MapPin size={22} weight="fill" className="text-orange-300 shrink-0 mt-0.5" />
                <address className="not-italic text-blue-50 leading-relaxed" data-testid="contact-address">
                  <div>{BRAND.address.line1},</div>
                  <div>{BRAND.address.line2},</div>
                  <div>{BRAND.address.line3},</div>
                  <div className="font-semibold text-white">{BRAND.address.city} – {BRAND.address.pincode}</div>
                </address>
              </div>

              {/* WhatsApp */}
              <div className="mb-4 flex items-start gap-3">
                <WhatsappLogo size={22} weight="fill" className="text-emerald-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">WhatsApp</div>
                  <a href={waLink()} target="_blank" rel="noreferrer" className="text-lg font-bold text-white hover:text-orange-300" data-testid="contact-whatsapp-number">
                    {BRAND.whatsapp}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="mb-6 flex items-start gap-3">
                <Clock size={22} weight="fill" className="text-orange-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-orange-200 font-semibold">Business Hours</div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${open ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
                    <span className="text-white font-semibold">{open ? "Open Now" : "Closed"}</span>
                    <span className="text-blue-100 text-sm">· {BRAND.hours}</span>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-2 pt-2">
                <a
                  href={waLink("Hi Priyanka Enterprises, I need help with a service.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                  data-testid="contact-cta-whatsapp"
                >
                  <WhatsappLogo size={14} weight="fill" /> WhatsApp Now
                </a>
                <a
                  href={BRAND.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white text-[#1e3a8a] text-sm font-semibold hover:bg-slate-100 transition-colors"
                  data-testid="contact-cta-directions"
                >
                  <MapPin size={14} weight="fill" /> Get Directions <ArrowUpRight size={12} />
                </a>
                <a
                  href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white/10 border border-white/25 text-white text-sm font-semibold hover:bg-white/20"
                  data-testid="contact-cta-call"
                >
                  <Phone size={14} weight="fill" /> Call
                </a>
              </div>
            </div>
          </div>

          {/* Right — quick facts */}
          <div className="md:col-span-2 p-8 md:p-10 space-y-4 bg-slate-50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                <EnvelopeSimple size={18} className="text-[#1e3a8a]" weight="fill" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Email</div>
                <a href={`mailto:${BRAND.email}`} className="font-semibold text-slate-900 hover:text-[#1e3a8a]" data-testid="contact-email-link">{BRAND.email}</a>
                <div className="text-xs text-slate-600 mt-0.5">For partnerships &amp; sales</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <CheckCircle size={18} className="text-emerald-600" weight="fill" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Fastest response</div>
                <div className="font-semibold text-slate-900">WhatsApp us</div>
                <div className="text-xs text-slate-600 mt-0.5">Typically replies in 5 minutes</div>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Services at this branch</div>
              <div className="flex flex-wrap gap-1.5">
                {["PAN Card", "Aadhaar", "Passport", "GST", "Rent Agreement", "UDYAM", "Voter ID", "ITR"].map((s) => (
                  <span key={s} className="inline-block px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11px] font-medium text-slate-700">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ================= Message form ================= */}
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-3">
          <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Send us a message</h3>
          <p className="text-sm text-slate-600">Fill the form and our team will call you back on your registered mobile within 30 minutes during business hours.</p>
          <ul className="text-sm space-y-2 pt-3">
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> Free consultation for any government service</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> Same-day response &amp; document guidance</li>
            <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> All information kept confidential (AES-256)</li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <Card className="p-6 md:p-8 bg-white border-slate-200 shadow-sm">
            {done ? (
              <div className="text-center py-8" data-testid="contact-success">
                <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-3" />
                <div className="text-xl font-bold text-slate-900 mb-1">Thanks, {form.name.split(" ")[0]}!</div>
                <div className="text-sm text-slate-600">We received your message and will reach out on <span className="font-semibold">{form.mobile}</span> within 30 minutes.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Your Name *</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11" placeholder="Full name" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Mobile *</Label>
                    <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="h-11" placeholder="10-digit mobile" data-testid="contact-mobile" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Email (optional)</Label>
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11" placeholder="you@example.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Message *</Label>
                  <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} placeholder="How can we help?" className="resize-none" data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ea580c] hover:bg-orange-700 text-white font-semibold gap-2" data-testid="contact-submit">
                  <PaperPlaneTilt size={14} weight="fill" /> {busy ? "Sending…" : "Send message"}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>

      {/* ================= Map iframe (best effort) ================= */}
      <div className="mt-14">
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 aspect-[16/6] relative" data-testid="contact-map">
          <iframe
            title="DigitalSetu — Thane Branch"
            src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddressForMap())}&output=embed`}
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <a
            href={BRAND.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-white text-[#1e3a8a] text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow"
          >
            <MapPin size={14} weight="fill" /> Open in Google Maps <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
    </div>
  );
}

function fullAddressForMap() {
  const a = BRAND.address;
  return `${BRAND.legalName}, ${a.line1}, ${a.line2}, ${a.line3}, ${a.city} ${a.pincode}, ${a.state}`;
}
